import React, { useState, useEffect } from 'react';

interface CSVData {
  headers: string[];
  rows: any[];
  mappedHeaders: { [key: string]: string };
}

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  csvData: CSVData | null;
  exportFieldSelection: { [key: string]: boolean };
  onFieldSelectionChange: (selection: { [key: string]: boolean }) => void;
  onExport: () => void;
  userCredits: number | null;
  onCheckCredits: () => Promise<void>;
  isExporting: boolean;
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  csvData,
  exportFieldSelection,
  onFieldSelectionChange,
  onExport,
  userCredits,
  onCheckCredits,
  isExporting
}) => {
  const [isCheckingCredits, setIsCheckingCredits] = useState(false);

  // Reset checking state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsCheckingCredits(false);
    }
  }, [isOpen]);

  // Auto-check credits when modal opens (export always requires payment)
  useEffect(() => {
    if (isOpen && userCredits === null && !isCheckingCredits) {
      setIsCheckingCredits(true);
      onCheckCredits()
        .catch((error) => {
          console.error('Failed to check credits:', error);
        })
        .finally(() => {
          setIsCheckingCredits(false);
        });
    }
  }, [isOpen, userCredits, isCheckingCredits, onCheckCredits]);

  if (!isOpen || !csvData) return null;

  const selectedCount = Object.values(exportFieldSelection).filter(Boolean).length;
  const totalCount = csvData.headers.length;

  const exportRequiresPayment = true; // Export always costs 1 credit
  const hasEnoughCredits = userCredits !== null && userCredits >= 1;

  const handleSelectAllToggle = () => {
    const allSelected = csvData.headers.every(header => exportFieldSelection[header]);
    const newSelection: { [key: string]: boolean } = {};
    csvData.headers.forEach(header => {
      newSelection[header] = !allSelected;
    });
    onFieldSelectionChange(newSelection);
  };

  const handleFieldToggle = (header: string, checked: boolean) => {
    onFieldSelectionChange({
      ...exportFieldSelection,
      [header]: checked
    });
  };

  const handleExportClick = () => {
    if (selectedCount === 0) {
      // This should be handled by parent component
      return;
    }

    onExport();
  };

  const getFieldTypeLabel = (header: string) => {
    if (!csvData.mappedHeaders) return 'Custom field';

    if (csvData.mappedHeaders.filename === header) return 'Filename field';
    if (csvData.mappedHeaders.title === header) return 'Title field';
    if (csvData.mappedHeaders.description === header) return 'Description field';
    if (csvData.mappedHeaders.keywords === header) return 'Keywords field';

    return 'Custom field';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Fields to Export</h3>
        <p className="text-sm text-gray-600 mb-4">
          Choose which fields to include in your exported CSV. All fields are selected by default.
        </p>

        {/* Export Cost Information */}
        {exportRequiresPayment && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 mb-2">
              <strong>💎 Export Cost: 1 credit</strong>
            </p>
            {isCheckingCredits ? (
              <div className="flex items-center text-sm text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Checking credit balance...
              </div>
            ) : userCredits !== null ? (
              hasEnoughCredits ? (
                <p className="text-sm text-green-700">
                  Your current balance: {userCredits} credits ✅
                </p>
              ) : (
                <div className="text-sm text-red-700">
                  <p className="font-semibold mb-1">⚠️ Insufficient credits</p>
                  <p>Your current balance: {userCredits} credits</p>
                  <p className="mt-1">Please buy more credits to export your CSV.</p>
                </div>
              )
            ) : (
              <p className="text-sm text-red-600">
                Failed to check credit balance. Please try again.
              </p>
            )}
          </div>
        )}

        <div className="space-y-4 mb-6">
          {/* Select All / Deselect All */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleSelectAllToggle}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {csvData.headers.every(header => exportFieldSelection[header]) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {csvData.headers.map(header => (
              <label key={header} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exportFieldSelection[header] || false}
                  onChange={(e) => handleFieldToggle(header, e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-800">{header}</span>
                  <div className="text-xs text-gray-500 mt-1">
                    {getFieldTypeLabel(header)}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedCount} of {totalCount} fields selected
          </div>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                {exportRequiresPayment && !hasEnoughCredits && userCredits !== null ? (
                  <button
                    onClick={() => {
                      onClose();
                      window.open('/pricing', '_blank');
                    }}
                    className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    Buy Credits
                  </button>
                ) : (
                  <button
                    onClick={handleExportClick}
                    disabled={isCheckingCredits || isExporting || (exportRequiresPayment && !hasEnoughCredits)}
                    className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                      isCheckingCredits || isExporting || (exportRequiresPayment && !hasEnoughCredits)
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isCheckingCredits ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Checking...
                      </div>
                    ) : isExporting ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Exporting...
                      </div>
                    ) : (
                      `Export ${selectedCount} Fields`
                    )}
                  </button>
                )}
              </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
